import React, { useEffect, useState } from "react";
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { getNetWorthHistory } from "../../api/plaidClient";
import { formatCurrencyCompact, formatDateShort } from "../../utils/formatters";
import ChartLoader from "../ui/ChartLoader";

function addZeroCrossings(data) {
    if (!data || data.length < 2) return data;

    const result = [];
    for (let i = 0; i < data.length; i++) {
        const current = data[i];
        result.push(current);

        if (i < data.length - 1) {
            const next = data[i + 1];
            const currVal = current.netWorth;
            const nextVal = next.netWorth;

            if ((currVal > 0 && nextVal < 0) || (currVal < 0 && nextVal > 0)) {
                const ratio = Math.abs(currVal) / (Math.abs(currVal) + Math.abs(nextVal));
                const currDate = new Date(current.date).getTime();
                const nextDate = new Date(next.date).getTime();
                const crossDate = new Date(currDate + ratio * (nextDate - currDate));

                result.push({
                    date: crossDate.toISOString().split('T')[0],
                    netWorth: 0,
                    isCrossing: true
                });
            }
        }
    }
    return result;
}

function splitData(data) {
    return data.map(item => ({
        ...item,
        positive: item.netWorth >= 0 ? item.netWorth : null,
        negative: item.netWorth <= 0 ? item.netWorth : null,
    }));
}

function CustomTooltip({ active, payload, label }) {
    if (active && payload && payload.length) {
        const dataPoint = payload[0]?.payload;
        const netWorth = dataPoint?.netWorth ?? payload[0]?.value ?? 0;
        const isNegative = netWorth < 0;

        return (
            <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-md">
                <p className="text-gray-500 text-xs mb-1">{formatDateShort(label)}</p>
                <p className={`font-semibold ${isNegative ? "text-danger-500" : "text-gray-900"}`}>
                    {formatCurrencyCompact(netWorth)}
                </p>
            </div>
        );
    }
    return null;
}

export default function NetWorthChart({ historyData: propData }) {
    const [data, setData] = useState(propData || []);
    const [loading, setLoading] = useState(!propData);
    const [error, setError] = useState(null);
    const [days, setDays] = useState(30);

    useEffect(() => {
        if (propData) {
            const cutoffDate = new Date();
            cutoffDate.setDate(cutoffDate.getDate() - days);
            const filtered = propData.filter(item => new Date(item.date) >= cutoffDate);
            setData(filtered);
            setLoading(false);
            return;
        }

        async function fetchHistory() {
            try {
                setLoading(true);
                setError(null);
                const history = await getNetWorthHistory(days);
                setData(history.history || []);
            } catch (e) {
                console.error("Error fetching net worth history:", e);
                setError("Could not load history");
            } finally {
                setLoading(false);
            }
        }

        fetchHistory();
    }, [days, propData]);

    const timeRanges = [
        { label: "30D", value: 30 },
        { label: "90D", value: 90 },
        { label: "1Y", value: 365 },
    ];

    if (loading) {
        return (
            <div className="card p-6">
                <div className="flex justify-between items-center mb-4">
                    <div className="h-5 bg-gray-100 rounded w-32 animate-pulse"></div>
                    <div className="h-8 bg-gray-100 rounded w-32 animate-pulse"></div>
                </div>
                <ChartLoader height={256} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="card p-6">
                <p className="text-gray-500 text-center">{error}</p>
            </div>
        );
    }

    if (data.length === 0) {
        return (
            <div className="card p-6">
                <p className="text-sm text-gray-500 mb-4">Net worth trend</p>
                <p className="text-gray-500 text-center py-8">
                    No history data yet. Check back after a few days.
                </p>
            </div>
        );
    }

    const processedData = splitData(addZeroCrossings(data));
    const hasNegative = data.some(d => d.netWorth < 0);

    return (
        <div className="card p-6">
            <div className="flex justify-between items-center mb-4">
                <p className="text-sm font-medium text-gray-700">Net worth trend</p>
                <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg">
                    {timeRanges.map((range) => (
                        <button
                            key={range.value}
                            onClick={() => setDays(range.value)}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                days === range.value
                                    ? "bg-white text-gray-900 shadow-sm"
                                    : "text-gray-500 hover:text-gray-900"
                            }`}
                        >
                            {range.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="h-64" style={{ willChange: "transform" }}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={processedData}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                    >
                        <defs>
                            <linearGradient id="positiveGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#2563EB" stopOpacity={0.2} />
                                <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="negativeGradient" x1="0" y1="1" x2="0" y2="0">
                                <stop offset="5%" stopColor="#EF4444" stopOpacity={0.2} />
                                <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <XAxis
                            dataKey="date"
                            tickFormatter={formatDateShort}
                            stroke="#94A3B8"
                            fontSize={12}
                            tickLine={false}
                            axisLine={false}
                        />
                        <YAxis
                            tickFormatter={formatCurrencyCompact}
                            stroke="#94A3B8"
                            fontSize={12}
                            tickLine={false}
                            axisLine={false}
                            width={80}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area
                            type="monotone"
                            dataKey="positive"
                            stroke="#2563EB"
                            strokeWidth={2}
                            fill="url(#positiveGradient)"
                            baseValue={0}
                            connectNulls={false}
                        />
                        {hasNegative && (
                            <Area
                                type="monotone"
                                dataKey="negative"
                                stroke="#EF4444"
                                strokeWidth={2}
                                fill="url(#negativeGradient)"
                                baseValue={0}
                                connectNulls={false}
                            />
                        )}
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
